import React, { ReactNode, RefObject, useEffect, useImperativeHandle, useRef, useState } from 'react';
import './Abookpicker.scss';
import { MdClose, MdMenu } from 'react-icons/md';
import Input from '../Input';
import clsx from 'clsx';
import AbookpickerModal from './AbookpickerModal/AbookpickerModal';
import { IAbookSource, IAbookTab, IExternalSearchAbook, IGridModalAttributes, ISetValues } from '@models/Forms/IForms';
import { IAddressBookData } from '@models/addressbook/IAddressBookData';
import { IAbookpickerRefActions } from './IAbookpickerRefActions';
import { AddressBookService } from '@services/AddressBookService';

export interface IAbookpickerProps<T extends HTMLElement = HTMLDivElement> {
    /** Имя поля */
    name?: string;
    /** */
    isMultiple: boolean;
    /** */
    title: string;
    /** зависимые поля*/
    setValues?: ISetValues;
    /** колонки для грида */
    gridAttribute?: IGridModalAttributes;
    /** Текст Placeholder */
    placeholder?: string;
    /** Значение по умолчанию */
    defaultValue?: any;
    /** отображаемое значение */
    displayValue: string;
    /** Скрыть поле */
    disabled?: boolean;
    /** Только для просмотра */
    readOnly?: boolean;
    /** Функция изменения значения */
    onChange?: (value: IAddressBookData[]) => Promise<void>;
    /** Кастомная кнопка */
    children?: ReactNode | ReactNode[];
    /** Переводит инпут в невалидный статус */
    invalid?: boolean;
    /**
     * Добавляет инпуту белый фон
     * @default true
     */
    filled?: boolean;
    /** Ссылка на контейнер портала */
    containerRef?: RefObject<T>;
    /** Реф для пробрасывания действий по кастомным кнопкам */
    controlRef?: React.Ref<IAbookpickerRefActions>;
    /**
     *  Добавлять фокус при выборе дат
     * @default false
     *  */
    isFocusBorder?: boolean;
    formValues: string;

    /**
     * Метод получения formValues
     * */
    getFormValues?: () => string;

    /**
     * Вкладки адресной книги
     * */
    tabsSettings?: IAbookTab[];

    /**
     * Дополнительная настройка источника данных (выбор данных из реквизитов-таблиц и т.д.)
     * */
    source?: IAbookSource;

    externalSearch?: IExternalSearchAbook;
    showChips: boolean;
    /**
     *  Отображение иконки сброса значения
     * @default true
     *  */
    showClearIcon?: boolean;
    displayType?: 'text' | 'default';
    insideGrid?: boolean;
    autoFocus?: boolean;
    wordWrapEnabled?: boolean;
}

// FIXME: Добавить управление с клавиатуры
const Abookpicker: React.FC<IAbookpickerProps> = ({
    name = 'Abookpicker',
    placeholder = 'Выберите значение',
    defaultValue,
    displayValue,
    gridAttribute,
    invalid = false,
    filled = true,
    disabled = false,
    readOnly = false,
    insideGrid = false,
    autoFocus,
    onChange,
    children,
    title,
    isMultiple,
    containerRef,
    controlRef,
    formValues,
    isFocusBorder = false,
    tabsSettings,
    source,
    externalSearch,
    showChips,
    showClearIcon = false,
    displayType = 'default',
    getFormValues = undefined,
    wordWrapEnabled,
}: IAbookpickerProps) => {
    useImperativeHandle(controlRef, () => ({
        reset: () => {},
        unSelect: (item: IAddressBookData) => {},
    }));

    // -------------------------------------------------------------------------------------------------------------------

    const AbookpickerRef = useRef<HTMLDivElement>(null);

    const [showAbook, toggleAbook] = useState<boolean>(false);

    // -------------------------------------------------------------------------------------------------------------------

    const [inputValue, setInputValue] = useState<number[]>();
    const [localDisplayValue, setLocalDisplayValue] = useState<string>(displayValue);

    useEffect(() => {
        setLocalDisplayValue(displayValue);
    }, [displayValue]);

    useEffect(() => {
        if (!defaultValue) {
            return;
        }

        if (source?.table) {
            let keys = (defaultValue as any[])?.map((row: any) => parseInt(row[source.key]));
            if (keys) {
                setInputValue(keys);
            }
        } else {
            if (defaultValue.toString().indexOf('|') > 0) {
                let arr = [] as number[];
                let arrStr = [] as string[];
                defaultValue
                    .toString()
                    .split('|')
                    .forEach((item: string) => {
                        arr.push(Number(item));
                        arrStr.push(item);
                    });

                setInputValue(arr);
            } else if (Array.isArray(defaultValue)) {
                let keys = (defaultValue as any[])?.map((row: any) => parseInt(row));
                if (keys) {
                    setInputValue(keys);
                    AddressBookService.fetchAbookItemsByKeys(keys.map((x) => x.toString())).then((response) => {
                        let data = response.data.map((x) => x.name).join(', ');
                        setLocalDisplayValue(data);
                    });
                }
            } else {
                let arr = [] as number[];
                arr.push(Number(defaultValue));

                setInputValue(arr);
            }
        }
    }, [defaultValue]);

    const clearHandler = async () => {
        setInputValue(undefined);
        onChange && (await onChange([]));
    };

    return (
        <div
            className={clsx(
                'rf-abookpicker',
                isFocusBorder && 'rf-abookpicker__focus-border',
                disabled && 'rf-abookpicker--disabled',
            )}
            ref={AbookpickerRef}
        >
            <div
                className={clsx('rf-abookpicker__input-wrapper', {
                    'rf-abookpicker__input-wrapper--disabled': disabled,
                    'rf-abookpicker__input-wrapper--readonly': readOnly,
                })}
                onFocus={autoFocus && !readOnly ? (e: any) => toggleAbook((prev) => !prev) : () => {}}
                onClick={!readOnly ? (e: any) => toggleAbook((prev) => !prev) : () => {}}
            >
                {children ||
                    (displayType == 'default' ? (
                        <Input
                            name={name}
                            value={localDisplayValue}
                            placeholder={placeholder}
                            disabled={disabled}
                            readOnly={true}
                            withoutClasses={!readOnly}
                            invalid={invalid}
                            filled={filled}
                            autoFocus={autoFocus}
                            endAdornment={
                                !readOnly && !disabled ? (
                                    <>
                                        {showClearIcon && (
                                            <div className={'rf-abookpicker__calendar-chevron'}>
                                                <MdClose
                                                    size="20"
                                                    onClick={async (e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        await clearHandler();
                                                    }}
                                                />
                                            </div>
                                        )}
                                        {!readOnly && !disabled ? (
                                            <div className={'rf-abookpicker__calendar-chevron'}>
                                                <MdMenu />
                                            </div>
                                        ) : (
                                            <></>
                                        )}
                                    </>
                                ) : undefined
                            }
                        />
                    ) : (
                        <div className="rf-abookpicker__displayType-text ">{localDisplayValue}</div>
                    ))}
            </div>

            {showAbook && (
                <AbookpickerModal
                    selected={inputValue!}
                    isMultiple={isMultiple}
                    title={title!}
                    externalSearch={externalSearch}
                    formValues={formValues}
                    getFormValues={getFormValues}
                    gridAttribute={gridAttribute}
                    onSubmitModal={onChange!}
                    onCloseModal={() => toggleAbook((prev) => !prev)}
                    tabsSettings={tabsSettings!}
                    showChips={showChips}
                    wordWrapEnabled={wordWrapEnabled}
                />
            )}
        </div>
    );
};

export default Abookpicker;
